<template>
  <div>
    <a v-if="viewSkipToMainContent" :href="`#${skipToMainContent}`" class="skip-link">{{ $t('dp.navbar.skipToMainContent') }}</a>

    <div v-if="isMultiLingual" class="d-lg-block d-none locales-banner">
      <div class="py-2 px-3 text-right">
        <a href="#" v-b-toggle.language-switcher>
          {{ $t('dp.navbar.language') }}
          <span class="when-open">
            <fa-icon :icon="['fas', 'chevron-up']" />
          </span>
          <span class="when-closed">
            <fa-icon :icon="['fas', 'chevron-down']" />
          </span>
        </a>
      </div>
      <b-collapse id="language-switcher">
        <div class="py-2 px-3 text-right locales-selector">
          <a
            v-for="supportedLocale in city.locales"
            :key="supportedLocale"
            :href="currentUrlInLocale(supportedLocale)"
            class="ml-4">
            {{ $t("dp.navbar.locales." + supportedLocale) }}
          </a>
          <!-- Google Translate Widget here -->
        </div>
      </b-collapse>
    </div>

    <b-navbar class="my-0 px-4 py-0 align-items-center justify-content-between" toggleable="lg">
      <div class="container " :class="{ 'image-only-header': isImageOnlyHeader }">
        <b-nav-toggle
          v-if="!isImageOnlyHeader"
          target="navbar-toggle-collapse"
          :label="$t('dp.navbar.label')"
          :title="$t('dp.navbar.title')"
          class="p-0 border-0"
        >
          <template #default="{ expanded }">
            <transition name="fade">
              <fa-icon v-if="expanded" :icon="['fas', 'x']" />
              <fa-icon v-else :icon="['fas', 'bars']" />
            </transition>
          </template>
        </b-nav-toggle>

        <!-- Desktop logo to resolver tab order-->
        <b-navbar-brand :href="logoRedirectUrl" class="logo mr-0 d-lg-block d-none">
          <b-img
            v-show="docupetLogo || logoUrl"
            class="header-logo img-fluid"
            :src="isImageOnlyHeader || !logoPath ? docupetLogo : logoUrl"
            :alt="$t('dp.navbar.logoDescription')"
          />
        </b-navbar-brand>

        <b-collapse
          v-if="!isImageOnlyHeader"
          id="navbar-toggle-collapse"
          is-nav
          class="flex-row d-lg-none"
        >
          <b-navbar-nav
            v-if="submenusClosed"
            ref="mobileMenuDropdown"
            class="ml-auto z-10"
          >
            <b-nav-form v-if="!isLoggedIn" class="d-lg-none">
              <b-button type="submit" aria-hidden="true" hidden />
              <div class="login-signup-container">
                <b-link
                  router-tag="button"
                  class="dp-btn-primary"
                  :href="generateFullUrl('/login')"
                >
                  <span class="dp-btn-primary-text">{{ $t('dp.navbar.login') }}</span>
                </b-link>
                <b-link
                  router-tag="button"
                  class="dp-btn-outlined"
                  :href="generateFullUrl(isNoLicensing ? '/signup/register' : '/signup')"
                >
                  <span class="dp-btn-outlined-text">{{ $t('dp.navbar.signup') }}</span>
                </b-link>
              </div>
            </b-nav-form>

            <b-nav-item class="d-lg-none" @click.stop="subMenu = 'account'">
              <div class="w-100">
                <span class="float-left">{{ $t('dp.navbar.yourAccount') }}</span>
                <fa-icon class="float-right" icon="fa-solid fa-chevron-right" />
              </div>
            </b-nav-item>

            <b-nav-item v-if="isLoggedIn" exact :href="generateFullUrl('/account')" :active="checkRoute? $route.name === 'Dashboard': false">
              {{ $t('dp.navbar.dashboard') }}
            </b-nav-item>

            <b-nav-item v-if="isDisplayLicensingPage && !isLoggedIn" :href="generateFullUrl('/licensing')">
              {{ city.htmlLicensingLinkTitle || $t('dp.navbar.licensing') }}
            </b-nav-item>

            <b-nav-item v-if="isDisplayHomeSafePage && !isLoggedIn" :href="generateFullUrl('/homesafe')">
              <span v-html="city.htmlHomesafeLinkTitle || $t('dp.navbar.homeSafe')"></span>
            </b-nav-item>

            <b-nav-item :href="generateFullUrl('/store')" :active="routePathContains('/store')" class="d-none d-lg-block">
              {{ $t('dp.navbar.shop') }}
            </b-nav-item>

            <b-nav-item class="d-lg-none" @click.stop="subMenu = 'shop'">
              <div class="w-100">
                <span class="float-left">{{ $t("dp.navbar.shop") }}</span>
                <fa-icon class="float-right" icon="fa-solid fa-chevron-right" />
              </div>
            </b-nav-item>

            <b-nav-item
              v-if="isLoggedIn"
              :href="generateFullUrl('/account/full/account-app/my-pets/')"
              :active="routePathContains('/my-pets')"
            >
              {{ $t('dp.navbar.myPets') }}
            </b-nav-item>

            <b-nav-item v-if="isDisplaySafeAndHappyFundPage && !isLoggedIn" :href="generateFullUrl('/safe-and-happy-fund')">
              {{ city.htmlSafeAndHappyFundLinkTitle || $t('dp.navbar.safeAndHappyFund') }}
            </b-nav-item>

            <b-nav-item v-if="!isLoggedIn && isDisplayAboutPage" :href="generateFullUrl('/about')">
              {{ $t('dp.navbar.about') }}
            </b-nav-item>

            <b-nav-item v-if="helpDeskUrl && isNoLicensing === false" :href="helpDeskUrl">
              {{ $t('dp.navbar.helpDesk') }}
            </b-nav-item>

            <b-nav-item
              v-if="!isLoggedIn"
              class="d-none d-lg-flex"
              :href="generateFullUrl(isNoLicensing ? '/signup/register' : '/signup')"
            >
              {{ $t('dp.navbar.signup') }}
            </b-nav-item>

            <b-nav-item-dropdown
              v-if="isMultiLingual"
              :text="$t('dp.navbar.language')"
              class="d-lg-none account-dropdown">
              <b-dropdown-item
                v-for="supportedLocale in city.locales"
                :key="supportedLocale"
                :href="currentUrlInLocale(supportedLocale)">
                {{ $t('dp.navbar.locales.' + supportedLocale) }}
              </b-dropdown-item>
              <b-dropdown-divider />
            </b-nav-item-dropdown>

            <b-nav-form class="nav-buttons nav-item d-none d-lg-flex ml-2">
              <b-button type="submit" aria-hidden="true" hidden />
              <b-link
                v-if="!isLoggedIn"
                router-tag="button"
                class="btn btn-login my-2 my-sm-0 mr-0 btn-outline-primary"
                :href="generateFullUrl('/login')"
              >
                <strong>{{ $t('dp.navbar.login') }}</strong>
              </b-link>

              <b-dropdown
                v-else
                variant="outline-primary"
                toggle-class="btn-account my-2 my-sm-0 mr-0 d-flex align-items-center"
                no-caret
                right
              >
                <template #button-content>
                  <span class="mr-2">{{ $t('dp.navbar.yourAccount') }}</span>
                  <fa-icon :icon="['far', 'angle-up']" class="align-bottom when-open" />
                  <fa-icon :icon="['far', 'angle-down']" class="align-bottom when-closed" />
                </template>
                <b-dropdown-item :href="generateFullUrl('/account/full/account-app/settings/my-details')">
                  {{ $t('dp.navbar.myDetails') }}
                </b-dropdown-item>
                <b-dropdown-item :href="generateFullUrl('/account/full/account-app/settings/pet-guardians')">
                  {{ $t('dp.navbar.petGuardians') }}
                </b-dropdown-item>
                <b-dropdown-item :href="generateFullUrl('/account/full/account-app/settings/profile-settings')">
                  {{ $t('dp.navbar.profileSettings') }}
                </b-dropdown-item>
                <b-dropdown-item :href="generateFullUrl('/account/full/account-app/settings/order-history')">
                  {{ $t('dp.navbar.orderHistory') }}
                </b-dropdown-item>
                <b-dropdown-item :href="generateFullUrl('/account/full/account-app/settings/billing-details')">
                  {{ $t('dp.navbar.billingDetails') }}
                </b-dropdown-item>
                <b-dropdown-item :href="generateFullUrl(`/logout?baseUrl=${baseLocaleUrl}`)">
                  {{ $t('dp.navbar.logOut') }}
                </b-dropdown-item>
              </b-dropdown>
            </b-nav-form>
          </b-navbar-nav>

          <!-- Account Mobile Menu -->
          <dp-navbar-submenu :title="$t('dp.navbar.yourAccount')" :base-locale-url="baseLocaleUrl" :is-shown="subMenu === 'account'" @close="subMenu = null">
            <b-nav-item :href="generateFullUrl('/account/full/account-app/settings/my-details')">
              {{ $t("dp.navbar.myDetails") }}
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/account/full/account-app/settings/pet-guardians')">
              {{ $t("dp.navbar.petGuardians") }}
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/account/full/account-app/settings/profile-settings')">
              {{ $t("dp.navbar.profileSettings") }}
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/account/full/account-app/settings/order-history')">
              {{ $t("dp.navbar.orderHistory") }}
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/account/full/account-app/settings/billing-details')">
              {{ $t("dp.navbar.billingDetails") }}
            </b-nav-item>
            <b-dropdown-divider style="margin-left: -1rem;" />
            <b-nav-item :href="generateFullUrl(`/logout?baseUrl=${baseLocaleUrl}`)" class="border-top-1 border-bottom-1 text-gray font-weight-normal">
              {{ $t("dp.navbar.logOut") }}
            </b-nav-item>
            <b-dropdown-divider style="margin-left: -1rem;" />
          </dp-navbar-submenu>

          <!-- Shop Mobile Menu -->
          <dp-navbar-submenu :title="$t('dp.navbar.shop')" :base-locale-url="baseLocaleUrl" :is-shown="subMenu === 'shop'" @close="subMenu = null">
            <b-nav-item :href="generateFullUrl('/store')" class="row">
              <span class="col-1" style="min-width: 3.7rem;"></span>
              <span class="col-11 p-0">
                {{ $t("dp.navbar.shopDropdown.shopAll") }}
              </span>
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/store/tags?redirectRouteName=docupet_petowner_order_store_tags')" class="row">
              <img
                style="max-width: 3.7rem;"
                class="col-1"
                src="https://us.docupet.com/media/uploads/public/system/fb/5f/fb5f55abce17b4f50056853f21469fa139106444a47e626c21dd5a78782fc376.svg"
                alt=""
              />
              <span class="col-11 pl-0">{{ $t("dp.navbar.shopDropdown.tags") }}</span>
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/store/smart-tracker')" class="row">
              <img
                style="max-width: 3.7rem;"
                class="col-1"
                src="https://us.docupet.com/media/uploads/public/system/ca/e2/cae28c1ed7971c8646c9ca9b65e66892ff4b18cd0cda464cc149519486843143.svg"
                alt=""
              />
              <span class="col-11 pl-0">{{ $t('dp.navbar.shopDropdown.smartTracker') }}</span>
            </b-nav-item>
            <b-nav-item
              :href="generateFullUrl('/store/wallet-cards?redirectRouteName=docupet_petowner_order_store_wallet_cards')"
              class="row"
            >
              <img
                style="max-width: 3.7rem;"
                class="col-1"
                src="https://us.docupet.com/media/uploads/public/system/a9/ca/a9ca2863d47f15fe877673584a09cbab0df5da92f2bef3e244e0f954a9f804ef.png"
                alt=""
              />
              <span class="col-11 pl-0">{{ $t("dp.navbar.shopDropdown.walletCards") }}</span>
            </b-nav-item>
            <b-nav-item :href="generateFullUrl('/store/collars?redirectRouteName=docupet_petowner_order_store_collars')" class="row">
              <img
                style="max-width: 3.7rem;"
                class="col-1"
                src="https://us.docupet.com/media/uploads/public/system/80/93/80934877866cefe3277fa66462888e17f4dcd1238bd87154ae8d1b0cd574932e.svg"
                alt=""
              />
              <span class="col-11 pl-0">{{ $t("dp.navbar.shopDropdown.collars") }}</span>
            </b-nav-item>
          </dp-navbar-submenu>
        </b-collapse>

        <!-- Mobile logo to resolver tab order -->
        <b-navbar-brand :href="logoRedirectUrl" class="logo mr-0 d-lg-none d-block">
          <b-img
            v-show="docupetLogo || logoUrl"
            class="header-logo img-fluid"
            :src="isImageOnlyHeader || !logoPath ? docupetLogo : logoUrl"
            :alt="$t('dp.navbar.logoDescription')"
          />
        </b-navbar-brand>

        <b-navbar-nav class="flex-row d-lg-none">
          <b-nav-item
            v-if="!isImageOnlyHeader"
            :href="generateFullUrl('/store')"
            :active="routePathContains('/store')"
            class="text-primary pr-2 py-0 has-icon"
            title="Shop"
          >
            <fa-icon class="fa-xl" :icon="['far', 'store']" />
          </b-nav-item>

          <b-nav-item
            v-if="!isImageOnlyHeader"
            class="basket py-0 has-icon"
            :href="generateFullUrl('/basket')"
            :aria-label="$t('dp.navbar.basket.ariaLabel', { basketCount: safeBasketCount })"
          >
            <span class="fa-layers fa-fw fa-xl">
              <fa-icon class="text-primary" :icon="['far', 'shopping-basket']" />
              <span class="fa-layers-counter fa-2x">{{ safeBasketCount }}</span>
            </span>
          </b-nav-item>
        </b-navbar-nav>

        <b-link
          v-if="!isImageOnlyHeader"
          class="basket d-none d-lg-block"
          :href="generateFullUrl('/basket')"
          :aria-label="$t('dp.navbar.basket.ariaLabel', { basketCount: safeBasketCount })"
        >
          <fa-icon class="float-right" :icon="['far', 'shopping-basket']" size="2x" />
          <b-badge class="position-relative rounded-circle badge-basket" variant="info" v-text="safeBasketCount" />
        </b-link>
      </div>
    </b-navbar>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { icons } from './assets/fontawesome.js';
import docupetLogo from './assets/docupet_logo.png';
import DpNavbarSubmenu from './NavbarSubmenu';
library.add(icons);

export default {
  name: 'DpNavbar',
  components: { DpNavbarSubmenu },
  props: {
    locale: {
      type: [String, null],
      required: false,
      default: null,
    },
    isLoggedIn: {
      type: Boolean,
      required: false,
      default: false,
    },
    city: {
      type: Object,
      required: false,
      default: null,
    },
    petOwner: {
      type: Object,
      required: false,
      default: null,
    },
    baseUrl: {
      type: String,
      required: true,
    },
    logoPath: {
      type: String,
      required: false,
      default: null,
    },
    orderId: {
      type: [String, Number],
      required: false,
      default: null,
    },
    basketCount: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    logoRedirectPath: {
      type: String,
      required: false,
      default: null,
    },
    isImageOnlyHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    helpDeskUrl: {
      type: String,
      required: false,
      default: 'https://docupetinc.zendesk.com/hc/en-us',
    },
    skipToMainContent: {
      type: String,
      required: false,
      default: 'main',
    },
    viewSkipToMainContent: {
      type: Boolean,
      required: false,
      default: true,
    },
    checkRoute: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      docupetLogo: docupetLogo,
      subMenu: null,
    };
  },
  computed: {
    safeBasketCount() {
      return Number.isSafeInteger(this.basketCount) && !(this.basketCount < 0)
        ? Number(this.basketCount) < 99
          ? this.basketCount.toString()
          : '99+'
        : '0';
    },
    baseLocaleUrl() {
      return this.generateFullUrl();
    },
    isMultiLingual() {
      return this.city && this.city.locales && this.city.locales.length > 1;
    },
    logoUrl() {
      // If the logo path is already a full URL don't include the base url.
      if (this.logoPath && this.logoPath.indexOf('.com') !== -1) {
        return this.logoPath;
      } else {
        return this.baseUrl + this.logoPath;
      }
    },
    logoRedirectUrl() {
      return this.logoRedirectPath
        ? this.logoRedirectPath
        : this.isLoggedIn
        ? this.generateFullUrl('/account/full/account-app')
        : this.baseUrl;
    },
    isDisplayHomeSafePage() {
      if (this.city) {
        return this.city.displayHomeSafePage;
      }
      return false;
    },
    isDisplaySafeAndHappyFundPage() {
      if (this.city) {
        return this.city.displaySafeAndHappyFundPage;
      }
      return false;
    },
    isDisplayLicensingPage() {
      if (this.petOwner && this.city && this.city.name === 'Boulder') {
        if (this.petOwner.vsLocation === 'COUNTY' || this.petOwner.vsLocation === 'OTHER') {
          return false;
        }
      }
      if (this.city) {
        return this.city.displayLicensingPage;
      }
      return false;
    },
    isNoLicensing() {
      if (this.city) {
        return this.city.noLicensing;
      }
      return true;
    },
    isDisplayAboutPage() {
      if (this.city) {
        return this.city.displayAboutPage;
      }
      return false;
    },
    submenusClosed() {
      return this.subMenu === null;
    },
  },
  mounted() {
    this.resolveAccessibilityIssues();
  },
  methods: {
    routePathContains(path) {
      return window?.location?.pathname?.includes(path);
    },
    resolveAccessibilityIssues() {
      this.$refs?.mobileMenuDropdown?.querySelector('.account-dropdown a')?.removeAttribute('aria-haspopup');
    },
    generateFullUrl(path) {
      return this.$store.getters.generateFullUrl(path);
    },
    currentUrlInLocale(locale) {
      const path = window.location.pathname.replace(/\/[a-z]{2}_[A-Z]{2}\//g, `/${locale}/`);
      return `${this.baseUrl}${path}${window.location.search}`;
    },
  },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/bootstrap-grid';
@import '../../assets/styles/_variables';

#navbar-toggle-collapse {
  order: 2;
}
.image-only-header {
  display: flex;
  justify-content: center !important;
}
.header-logo {
  max-height: 2rem;
}
.navbar {
  min-height: 70px;
  z-index: 1000;
  background-color: $dp-white;
}
.mini-toggle {
  display: none;
}
.skip-link {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  z-index: -1;
  padding: 8px;
  text-decoration: none;
  background-color: $dp-white;
  border: 1px solid $dp-navy;
  &:focus {
    opacity: 1;
    z-index: 9999;
  }
}
.locales-banner > div,
.navbar .container {
  max-width: 1824px;
}
.navbar-light .navbar-nav .nav-item {
  font-weight: bold;
}
.navbar-light .navbar-nav .nav-link,
.navbar-light .navbar-text,
.dropdown-item {
  color: $dp-navy !important;
  font-size: 15.4px;
  @include media-breakpoint-down(lg) {
    font-size: 18px;
    line-height: 1.5;
  }
  &:hover {
    color: $dp-blue !important;
  }
  &:active {
    background-color: $dp-gray-1 !important;
  }
}
.dropdown-item {
  &:hover,
  &:focus {
    background-color: $dp-light-blue-2 !important;
  }
}
.navbar-toggler {
  width: 23px;
  height: 23px;
}
.nav-buttons .nav-link {
  padding: 0 !important;
}
.navbar-light .navbar-nav {
  & button {
    font-size: 14.4px;
    &:not(.btn-account) {
      padding: 6px 25px;
      font-weight: bold;
      text-transform: uppercase;
    }
    &.btn-account,
    &.btn-account {
      margin: 10px;
      padding: 10px 14px;
      border-radius: 6.5px;
      background-color: $dp-white !important;
      color: $dp-navy !important;
      &:hover {
        color: $dp-blue !important;
      }
    }
    &[aria-expanded='false'] .when-open,
    &[aria-expanded='true'] .when-closed {
      display: none;
    }
    @media screen and (max-width: 991px) {
      margin-top: 0 !important;
    }
  }
  & .btn-login {
    border-radius: 6.5px;
    font-size: 14.4px;
    padding: 10px 34px;
    margin-left: 0.5rem;
  }
}
.dropdown-menu {
  padding-bottom: 0.25rem !important;
  padding-top: 0.25rem !important;
}
.nav-buttons {
  .dropdown-menu {
    border-radius: 0.5rem;
    li {
      padding: 0;
      a.dropdown-item {
        padding: 0.5rem 1rem;
        color:  #454545;
        font-size: 1rem;
        line-height: 1.5rem;
      }
      &:last-child {
        border-top: 1px solid #D8D8D8;
      }
    }
  }
}
.basket {
  color: $dp-navy;
  & .badge-basket {
    font-size: 1em;
    top: -0.75rem;
    left: 2.8rem;
  }
  &:hover {
    color: $dp-blue;
  }
  & .fa-layers-counter {
    background-color: $dp-yellow;
    color: #212529;
    top: -8px;
    right: -8px;
  }
  @include media-breakpoint-up(lg) {
    order: 3;
  }
}
.navbar-light .navbar-nav {
  & .nav-item {
    margin-right: 0;
    margin-left: 0;
    &.has-icon {
      .nav-link {
        &.active {
          border-bottom: none;
        }
      }
    }
    .nav-link {
      height: 100%;
      display: flex;
      text-align: center;
      align-items: center;
    }
  }

  @include media-breakpoint-up(lg) {
    gap: 1rem;
    height: 5rem;
    & .nav-item {
      margin-right: 0;
      margin-left: 0;
    }

    .nav-link {
      padding-right: 0 !important;
      padding-left: 0 !important;
      &.active {
        color: $dp-navy;
        font-weight: bold;
        border-bottom: 4px solid $dp-navy;
        padding-bottom: 12px;
      }
    }
  }
}

.locales-banner {
  background-color: #f0f4ff;

  a {
    font-size: $font-size-sm;
    color: $dp-text;
  }

  [aria-expanded='false'] .when-open,
  [aria-expanded='true'] .when-closed {
    display: none;
  }

  .locales-selector {
    border-top: 1px solid $dp-white;
  }

  &>div {
    margin: 0 auto;
  }
}

.nav-form-full-width {
  width: 100%;
}

.login-signup-container {
  display: flex;
  width: 100%;
  gap: 12px;
  padding: 0 16px;
}

.dp-btn-base {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
  gap: 8px;
  border-radius: 8px;
  text-align: center;
  flex: none;
  flex-grow: 1;
}

.dp-btn-outlined {
  @extend .dp-btn-base;
  box-sizing: border-box;
  border: 1px solid #0A5D94;
  background: #fff;
  color: #0A5D94;
  order: 1;
}

.dp-btn-primary {
  @extend .dp-btn-base;
  background: #0D78C5;
  border: none;
  color: #FFFFFF;
  order: 0;
}

.dp-btn-text {
  text-align: center;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.dp-btn-primary-text {
  @extend .dp-btn-text;
  color: #FFFFFF;
}

.dp-btn-outlined-text {
  @extend .dp-btn-text;
  color: #0A5D94;
}

.justify-center {
  justify-content: center;
}

@media screen and (max-width: 280px) {
  .logo {
    max-width: 130px;
  }
  .header-logo {
    max-height: 30px;
  }
  .navbar {
    min-height: auto;
  }
}
@media screen and (max-width: 430px) {
  .header-logo {
    max-height: 30px;
  }
  .navbar {
    min-height: 60px;
  }
}
@media screen and (max-width: 991px) {
  .navbar.navbar-light .navbar-collapse {
    & .navbar-nav {
      & li {
        &.form-inline {
          padding: 1rem 1rem 1rem 0;
          &.btn {
            margin-top: 0;
          }
          & form.form-inline div.d-inline .btn.btn-mobile-nav {
            border-radius: 6.5px;
            margin-right: 15px;
            font-size: 14.4px;
            padding: 10px 34px;
            text-transform: capitalize;
            font-weight: normal;
          }
        }
        &.nav-item {
          &.b-nav-dropdown.dropdown.show > a {
            padding-bottom: 0 !important;
          }
          & .dropdown-menu.show {
            border: none;
          }
        }
      }
    }
  }
}
</style>
